/**
 * driverLicense.js — source unique FRONTEND pour trois normalisations du
 * domaine « conducteur » (chantier Groupe multi-pays) :
 *
 *   1. l'email bouche-trou `pasdemailNNN@non-renseigne.invalid` posé quand un
 *      fichier client n'a pas d'adresse (le schéma `driver` impose `email`) ;
 *      TLD `.invalid` réservé RFC 2606 → non routable, aucun mail ne peut
 *      partir par erreur. C'est VOULU : ne pas remplacer par un domaine réel.
 *   2. les catégories de permis harmonisées UE (directive 2006/126/CE) ;
 *   3. le pays de délivrance du permis (ISO 3166-1 alpha-2).
 *
 * ⚠️ Jumeau backend : `backend/shared/driver_license.mjs`. Le build CRA n'autorise
 * pas d'import hors de `src/`, d'où la duplication assumée — une seule définition
 * de chaque motif PAR CÔTÉ. Toute évolution ici se reporte là.
 */

// ============================================================================
// 1. Email bouche-trou
// ============================================================================

export const PLACEHOLDER_EMAIL_DOMAIN = 'non-renseigne.invalid';

/** L'unique définition du motif, côté backend. */
const PLACEHOLDER_EMAIL_RE = /^pasdemail(\d+)@non-renseigne\.invalid$/i;

/** Vrai si l'adresse est un bouche-trou posé par l'import (jamais une vraie adresse). */
export function isPlaceholderEmail(email) {
  if (!email) return false;
  return PLACEHOLDER_EMAIL_RE.test(String(email).trim());
}

/** Numéro de séquence d'un bouche-trou, ou 0 si l'adresse n'en est pas un. */
export function placeholderEmailSeq(email) {
  if (!email) return 0;
  const m = String(email).trim().match(PLACEHOLDER_EMAIL_RE);
  return m ? parseInt(m[1], 10) || 0 : 0;
}

/** Fabrique `pasdemail001@non-renseigne.invalid` (3 chiffres mini). */
export function buildPlaceholderEmail(seq) {
  return `pasdemail${String(seq).padStart(3, '0')}@${PLACEHOLDER_EMAIL_DOMAIN}`;
}

/**
 * Plus grand numéro déjà posé dans un lot de conducteurs existants.
 * Sert à repartir de là, pour ne jamais recommencer à 001 dans une org.
 */
export function maxPlaceholderSeq(drivers = []) {
  let max = 0;
  for (const d of drivers) {
    const n = placeholderEmailSeq(d?.email);
    if (n > max) max = n;
  }
  return max;
}

// ============================================================================
// 2. Catégories de permis — directive 2006/126/CE (permis UE harmonisé)
// ============================================================================

/** Les 15 catégories communes à tous les États membres, dans l'ordre officiel. */
export const EU_LICENSE_CATEGORIES = [
  'AM', 'A1', 'A2', 'A', 'B1', 'B', 'BE',
  'C1', 'C1E', 'C', 'CE', 'D1', 'D1E', 'D', 'DE',
];

const EU_SET = new Set(EU_LICENSE_CATEGORIES);

/**
 * Libellés longs → catégorie. Appliqués AVANT le découpage, du plus long au
 * plus court (« semi-remorque » doit gagner sur « remorque »).
 */
const CATEGORY_PHRASES = [
  ['transport en commun', 'D'],
  ['ensemble routier', 'CE'],
  ['semi remorque', 'CE'],
  ['super lourd', 'CE'],
  ['poids lourd', 'C'],
  ['cyclomoteur', 'AM'],
  ['remorque', 'BE'],
  ['autocar', 'D'],
  ['autobus', 'D'],
  ['bus', 'D'],
  ['moto', 'A'],
];

/** Mots de décoration à jeter : « kat. B », « behörighet CE », « permis B »… */
const CATEGORY_NOISE = [
  'prawo jazdy', 'prawojazdy', 'kategoria', 'kategorie', 'kategorii', 'kat',
  'behorighet', 'behorigheter', 'korkort',
  'permis', 'categorie', 'categories', 'classe', 'class',
  'license', 'licence', 'licenses', 'licences', 'driving', 'driver',
  'fuhrerschein', 'klasse',
  // conjonctions (« poids lourds ET remorque ») — jamais 'de', 'am' ni 'a' :
  // ce sont des catégories UE, les jeter ferait perdre l'information.
  'et', 'and', 'och', 'oraz', 'samt', 'ou', 'avec', 'plus',
];

/**
 * Minuscules + désaccentuation. NFD ne décompose pas tout : le `ł` polonais et
 * quelques nordiques sont traités à la main (mesuré, pas supposé).
 */
function asciiFold(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[łŁ]/g, 'l')
    .replace(/[øØ]/g, 'o')
    .replace(/[åÅ]/g, 'a')
    .replace(/[æÆ]/g, 'ae')
    .replace(/[đĐ]/g, 'd')
    .replace(/[ßẞ]/g, 'ss')
    .toLowerCase();
}

export { asciiFold };

/**
 * Parse une saisie libre en catégories UE.
 * Accepte « B,CE », « kat. B », « behörighet CE », « poids lourd », « B ; remorque ».
 * @returns {{categories: string[], unknown: string[]}} — `unknown` = jetons non
 *   reconnus, à signaler en AVERTISSEMENT (jamais en échec de ligne).
 */
export function parseLicenseCategories(raw) {
  const out = [];
  const unknown = [];
  if (raw == null || raw === '') return { categories: out, unknown };

  let s = asciiFold(raw)
    .replace(/[.’'"()[\]]/g, ' ')    // points, apostrophes, parenthèses, crochets
    .replace(/[-_/\\|+&;,]/g, ' , ')       // tous les séparateurs → virgule
    .replace(/\s+/g, ' ')
    .trim();

  // Libellés longs d'abord (pluriel toléré).
  for (const [phrase, code] of CATEGORY_PHRASES) {
    const re = new RegExp(`(?<![a-z0-9])${phrase.replace(/ /g, '(?: , | )')}s?(?![a-z0-9])`, 'g');
    s = s.replace(re, ` , ${code.toLowerCase()} , `);
  }

  // Décoration.
  for (const noise of CATEGORY_NOISE) {
    const re = new RegExp(`(?<![a-z0-9])${noise.replace(/ /g, '\\s+')}(?![a-z0-9])`, 'g');
    s = s.replace(re, ' ');
  }

  for (const tok of s.split(/[^a-z0-9]+/)) {
    if (!tok) continue;
    const code = tok.toUpperCase();
    if (EU_SET.has(code)) {
      if (!out.includes(code)) out.push(code);
    } else if (!unknown.includes(tok.toUpperCase())) {
      unknown.push(tok.toUpperCase());
    }
  }

  // Ordre officiel, pour que le champ texte soit stable et comparable.
  out.sort((a, b) => EU_LICENSE_CATEGORIES.indexOf(a) - EU_LICENSE_CATEGORIES.indexOf(b));
  return { categories: out, unknown };
}

/** Liste normalisée → chaîne du champ `driver.license_categories` (`B,BE,C1E`). */
export function categoriesToText(categories = []) {
  return categories.join(',');
}

/** Chaîne stockée → liste (lecture du champ texte). */
export function textToCategories(text) {
  return parseLicenseCategories(text).categories;
}

/**
 * Les 4 booléens historiques, DÉRIVÉS du champ texte : du code et des écrans
 * les lisent encore, ils doivent rester synchronisés (jamais l'inverse).
 */
export function categoriesToBooleans(categories = []) {
  const has = c => categories.includes(c);
  return {
    license_category_b: has('B'),
    license_category_c: has('C'),
    license_category_d: has('D'),
    license_category_ce: has('CE'),
  };
}

/**
 * Reporte l'état des 4 cases historiques DANS la liste complète, sans perdre
 * les catégories qu'elles ne savent pas exprimer (C1E, D1, AM…). Utilisé par
 * les écrans qui n'offrent que ces 4 cases : cocher « B » ne doit pas effacer
 * le « C1E » saisi ailleurs.
 */
export function syncBooleansIntoCategories(categories = [], booleans = {}) {
  const truthy = v => v === true || v === 1 || /^(1|true|o|oui|x|y|yes)$/i.test(String(v ?? ''));
  const out = categories.filter(c => !['B', 'C', 'D', 'CE'].includes(c));
  if (truthy(booleans.license_category_b)) out.push('B');
  if (truthy(booleans.license_category_c)) out.push('C');
  if (truthy(booleans.license_category_d)) out.push('D');
  if (truthy(booleans.license_category_ce)) out.push('CE');
  out.sort((a, b) => EU_LICENSE_CATEGORIES.indexOf(a) - EU_LICENSE_CATEGORIES.indexOf(b));
  return out;
}

/** Les 4 booléens → liste (reprise d'un conducteur créé avant le champ texte). */
export function booleansToCategories(row = {}) {
  const truthy = v => v === true || v === 1 || /^(1|true|o|oui|x|y|yes)$/i.test(String(v ?? ''));
  const out = [];
  if (truthy(row.license_category_b)) out.push('B');
  if (truthy(row.license_category_c)) out.push('C');
  if (truthy(row.license_category_ce)) out.push('CE');
  if (truthy(row.license_category_d)) out.push('D');
  out.sort((a, b) => EU_LICENSE_CATEGORIES.indexOf(a) - EU_LICENSE_CATEGORIES.indexOf(b));
  return out;
}

// ============================================================================
// 3. Pays de délivrance du permis — ISO 3166-1 alpha-2
// ============================================================================

/** Noms acceptés en entrée (FR / EN / langue locale), normalisés sans accent. */
const COUNTRY_NAMES = {
  france: 'FR',
  pologne: 'PL', polska: 'PL', poland: 'PL', polen: 'PL',
  suede: 'SE', sverige: 'SE', sweden: 'SE', schweden: 'SE',
  allemagne: 'DE', deutschland: 'DE', germany: 'DE', niemcy: 'DE',
  belgique: 'BE', belgie: 'BE', belgium: 'BE',
  espagne: 'ES', espana: 'ES', spain: 'ES',
  italie: 'IT', italia: 'IT', italy: 'IT',
  'pays bas': 'NL', nederland: 'NL', netherlands: 'NL', hollande: 'NL',
  portugal: 'PT',
  'royaume uni': 'GB', 'united kingdom': 'GB', angleterre: 'GB',
  luxembourg: 'LU',
  suisse: 'CH', switzerland: 'CH', schweiz: 'CH',
  autriche: 'AT', austria: 'AT', osterreich: 'AT',
  roumanie: 'RO', romania: 'RO',
  'republique tcheque': 'CZ', czechia: 'CZ', tchequie: 'CZ',
  danemark: 'DK', denmark: 'DK', danmark: 'DK',
  finlande: 'FI', finland: 'FI', suomi: 'FI',
  norvege: 'NO', norway: 'NO', norge: 'NO',
  irlande: 'IE', ireland: 'IE',
  hongrie: 'HU', hungary: 'HU',
  slovaquie: 'SK', slovakia: 'SK',
  slovenie: 'SI', slovenia: 'SI',
  croatie: 'HR', croatia: 'HR',
  lituanie: 'LT', lithuania: 'LT',
  lettonie: 'LV', latvia: 'LV',
  estonie: 'EE', estonia: 'EE',
  bulgarie: 'BG', bulgaria: 'BG',
  grece: 'GR', greece: 'GR',
  maroc: 'MA', morocco: 'MA',
  tunisie: 'TN', tunisia: 'TN',
  algerie: 'DZ', algeria: 'DZ',
  turquie: 'TR', turkey: 'TR', turkiye: 'TR',
  ukraine: 'UA',
};

/**
 * Code ISO ou nom de pays → alpha-2 majuscule. `null` si non reconnu :
 * une ligne sans pays ou avec un pays illisible ne doit PAS échouer.
 */
export function normalizeLicenseCountry(raw) {
  if (raw == null || raw === '') return null;
  const s = asciiFold(raw).replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  if (/^[a-z]{2}$/.test(s)) return s.toUpperCase();
  return COUNTRY_NAMES[s] || null;
}
